<template>
  <v-card>
    <v-card-title class="px-6">
      <slot name="title-prepend" />
      <div class="d-flex align-center justify-space-between mt-6">
        <div class="text-secondary">
          <quote-awarded-chip :order="order" :quote="quote" />
          <quote-outdated-chip class="d-inline-block mb-1" :quote="quote" />
          <div class="qtm-h1" v-text="quote.vendor?.name" />
          <div v-if="priceGiven" class="mt-4 qtm-title-bold" v-text="totalPrice" />
        </div>
        <div v-if="showActions" class="d-flex align-center">
          <div v-if="!order.orderType?.fromBom">
            <qtm-btn v-if="order.canAwardQuote && priceGiven" secondary @click="previewPO = true">
              <v-icon location="left">
                mdi-text-box-search-outline
              </v-icon>
              Preview PO
            </qtm-btn>
            <qtm-btn
              v-else-if="order.hasPurchaseOrder && order.requiresAdjustments && !quote.is_winning_quote && priceGiven"
              secondary
              @click="showChangeAwardedQuoteConfirmDialog = true"
            >
              Select this quote instead
            </qtm-btn>
            <quote-items-btn
              v-if="priceGiven"
              class="ml-1"
              :order="order"
              :quote="quote"
              @update:order="$emit('update:order', $event)"
            />
          </div>
          <qtm-btn
            v-if="canUpload"
            class="px-0"
            density="compact"
            tertiary
            :to="{ name: 'orders-id-quotes-quote', params: { id: order.id, quote: quote.id } }"
          >
            <v-icon class="mr-2">
              mdi-tray-arrow-up
            </v-icon>
            Upload Quote
            <v-tooltip
              activator="parent"
              content-class="bg-mid-dark-grey pa-2 rounded-sm text-center text-white"
              location="top"
              max-width="300"
              opacity="1"
            >
              Upload the quote manually if you received it directly from the supplier.
            </v-tooltip>
          </qtm-btn>
          <v-menu v-if="priceGiven && !isWinningQuote" content-class="qtm-border" tile>
            <template v-slot:activator="{ props: activator }">
              <qtm-icon-btn
                v-bind="activator"
                class="ml-1"
                color="primary"
                icon="mdi-dots-horizontal"
                rounded="0"
                variant="outlined"
              />
            </template>
            <v-list class="py-0">
              <v-list-item
                class="py-0 px-6"
                exact
                :to="editRoute"
                title="Edit Quote"
              />
              <v-list-item
                v-if="order.orderType?.fromBom"
                class="py-0 px-6"
                exact
                :to="matchRoute"
                title="Match Items"
              />
            </v-list>
          </v-menu>
        </div>
      </div>
      <preview-po-dialog :id="quote.id" v-model="previewPO" quote />
      <change-awarded-quote-confirm-dialog
        v-if="showChangeAwardedQuoteConfirmDialog"
        v-model="showChangeAwardedQuoteConfirmDialog"
        :quote="quote"
        :order="order"
        :loading="loading"
        @confirm="changeAwardedQuote"
      />
    </v-card-title>

    <v-card-text class="pt-0 px-6">
      <quote-attachments-link v-if="quote.attachments.length" :quote="quote" />
      <details-container class="mt-2" :details="details" label-size="9em">
        <template v-slot:contact="{ text }">
          {{ text }}
          <out-of-office-status v-if="quote.salesperson?.ooo_date_started" class="mx-2" />
          <change-contact-dialog
            v-if="order.state.can_change_quote_vendor && quote.vendor"
            v-model="quote.salesperson"
            :save="changeContact"
            save-label="Resend Quote Request"
            :vendor="quote.vendor"
          />
        </template>
      </details-container>
      <purchase-order-items-table
        v-if="priceGiven"
        class="mt-6"
        :delivery-charge="quote.delivery_charge"
        for-quote
        :items="items"
        :pickup-charge="quote.pickup_charge"
        total-label="Total"
        :total-price="quote.total_price"
      />
      <quote-response
        v-else
        class="bg-background mt-4"
        :response="String(quote.response_received)"
        style="height: 110px"
      />
      <div v-if="quote.comments">
        <div class="mt-4 qtm-h4 text-secondary">
          Supplier Comment
        </div>
        <div v-linkify="quote.comments" />
      </div>
      <v-divider class="my-6" />
      <supplier-chat :order="order" :quote="quote" />
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import type { Contact, Order, Quote } from '@quotetome/materials-api'
import ChangeAwardedQuoteConfirmDialog from '@/components/purchase-orders/change-awarded-quote-confirm-dialog.vue'
import DetailsContainer from '@/components/details-container.vue'
import OutOfOfficeStatus from '@/components/vendors/out-of-office-status.vue'
import PreviewPoDialog from '@/components/purchase-orders/preview-po-dialog.vue'
import PurchaseOrderItemsTable from '@/components/purchase-orders/purchase-order-items-table.vue'
import QuoteAttachmentsLink from '@/components/quotes/quote-attachments-link.vue'
import QuoteAwardedChip from '@/components/quotes/quote-awarded-chip.vue'
import QuoteItemsBtn from '@/components/quotes/quote-items-btn.vue'
import QuoteOutdatedChip from '@/components/quotes/quote-outdated-chip.vue'
import QuoteResponse from '@/components/quotes/quote-response.vue'
import ChangeContactDialog from '@/components/vendors/change-contact-dialog.vue'
import SupplierChat from '@/components/vendors/supplier-chat.vue'
import { currency } from '@/models/filters'

export interface Props {
  order: Order
  quote: Quote
  showActions?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update:order'])

const route = useRoute()
const matchRoute = {
  name: 'orders-id-quotes-quote-match-items',
  params: { id: props.order.id, quote: props.quote.id },
  query: { back: route.path },
}
const editRoute = {
  name: 'orders-id-quotes-quote',
  params: { id: props.order.id, quote: props.quote.id },
  query: { back: route.path },
}

const loading = ref(false)
const previewPO = ref(false)
const showChangeAwardedQuoteConfirmDialog = ref(false)

const canUpload = computed(() => !props.quote.response_received || props.quote.response_received === 'awaiting price')
const details = computed(() => [
  {
    label: props.order.customer_pickup ? 'Pick-up Date' : 'Delivery Date',
    value: 'delivery',
    include: priceGiven.value
      && (props.order.delivery_required || props.order.customer_pickup)
      && props.quote.delivery_available,
  },
  {
    label: 'Supplier Contact',
    text: props.quote.salesperson
      ? `${props.quote.salesperson.first_name} ${props.quote.salesperson.last_name} (${props.quote.salesperson.email})`
      : '',
    value: 'contact',
    include: true,
  },
  {
    label: 'Supplier Reference',
    text: props.quote.supplier_reference,
    value: 'ref',
    include: props.quote.supplier_reference,
  },
].filter(entry => entry.include))
const isWinningQuote = computed(() => props.quote.is_winning_quote)
const items = computed(() => props.quote.skus.map((item, i) => ({ ...item, item_number: i + 1 })))
const priceGiven = computed(() => props.quote.response_received === 'price given')
const totalPrice = computed(() => currency(props.quote.total_price))

const router = useRouter()
const { $api, $error, $toast } = useNuxtApp()
const changeAwardedQuote = async () => {
  loading.value = true
  try {
    const order = await $api.v1.rfqs.awardQuote(props.order.id as number, props.quote.id as number)

    emit('update:order', order)
    router.push({ name: 'orders-id-change-order', params: { id: props.order.id } })
  }
  catch (error) {
    $error.report(error)
  }

  showChangeAwardedQuoteConfirmDialog.value = false
  loading.value = false
}
const changeContact = (contact: Contact) => {
  return $api.v1.quotes.changeVendor(props.quote.id!, {
    contact_id: contact.id,
    send_invitation_email: true,
    vendor_id: props.quote.vendor!.id
  }).then(() => {
    $toast.success("Quote request sent to the new contact")
  })
}
</script>
